import FlexBox from '@/atomic-components/FlexBox';
import { getColor } from '@/utils/theme/colors';
import { getStyle } from '@/utils/theme/fonts';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import KycRequirementInfoIcon from '@/assets/pdp/KycRequirementInfoIcon.svg';
import GrayArrowWithWhiteBg from '@/assets/arrows/GrayArrowWithWhiteBg.svg';
import CloseIcon from '@/assets/CloseIcon.svg';
import FaqsDrawer from '@/components/Drawers/FaqsDrawer';
import KycArrow from './kycArrow.svg';
import TickMark from './TickMark.svg';
import DeliveryTruck from './Truck.svg';
import DrawerTruck from './DrawerTruck.svg';
import { useLocation } from '@/hooks/useLocation';
import { dataLayerPushWithEvent } from '@/utils/dataLayer';


const kycFaqs = [
	{
		question:
			'What is the timeline for completing the KYC process after placing an order ?',
		answer:
			'In most cases, the process is completed instantly and you will receive a confirmation notification right away. However, if additional documents are needed, we may need up to 24 hours from receiving all the necessary documents to complete the verification process.',
	},
	{
		question:
			'Can I complete the KYC process online or do I need to visit a physical location ?',
		answer:
			'We have made the KYC process completely online for your convenience.You can complete the process through the Furlenco app from the comfort of your home or office, without needing to visit any physical location.We hope you find the process quick and easy!',
	},
	{
		question: 'How often do I need to complete the KYC process ?',
		answer:
			'KYC needs to be completed only once at the time of placing your first order.However, if your account needs re - verification, the app will throw a prompt to complete the KYC process again.',
	},
	{
		question:
			'Will I be able to rent furniture and appliances if my KYC process is not completed?',
		answer:
			"We are committed to ensuring that everyone can rent furniture and appliances with us.While completing the KYC process is mandatory, we understand that sometimes there may be challenges.We will do everything we can to help you complete the process, including assessing and collecting additional documents if needed.However, please note that a PAN Card is mandatory for this process.In rare cases, where the KYC process does not meet our requirements, you may not be able to rent with us.But don't worry, we will work with you every step of the way to find a solution that works for you.",
	},
	{
		question: 'What documents do I need to submit for the KYC process?',
		answer:
			'You will need to provide a government-issued document each for your identity proof and address proof.',
	},
	{
		question: 'By when do I need to complete the KYC process ?',
		answer:
			"To ensure smooth delivery and installation, it is recommended to complete the KYC process immediately after placing your order.\n\nPlease note, we can only schedule your delivery after you've completed the KYC process. ",
	},
];

const KycRequirementInfoCard = ({lineOfProduct,promisedDate}:{lineOfProduct:string,promisedDate:string}) => {
	const {selectedLocation}=useLocation();

	const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false);
	const [isDeliveryDrawerVisible, setIsDeliveryDrawerVisible] =
		useState<boolean>(false);
	const openKycFaqDrawer = () => setIsDrawerVisible(true);
	const closeKycFaqDrawer = () => setIsDrawerVisible(false);
	const openDeliveryFaqDrawer = () => {dataLayerPushWithEvent('moe_event', {
		moe_event_name: 'Delivery Date Info Clicked'
	});setIsDeliveryDrawerVisible(true);}
	const closeDeliveryFaqDrawer = () => setIsDeliveryDrawerVisible(false);


	return (
		<>
			<FaqsDrawer
				drawerProps={{
					anchor: 'right',
					open: isDrawerVisible,
					onClose: closeKycFaqDrawer,
					PaperProps: { sx: { width: '460px', p: '80px' } },
				}}
				title={
					<Box width='100%'>
						<FlexBox justifyContent='space-between' alignItems='center'>
							<KycRequirementInfoIcon />
							<IconButton onClick={closeKycFaqDrawer}>
								<CloseIcon />
							</IconButton>
						</FlexBox>
						<Typography mt='32px' variant='H9A_Regular'>
							KYC Verification
						</Typography>
					</Box>
				}
				faqs={kycFaqs}
			/>
			<FlexBox
				sx={{
					background:'white',
					justifyContent: 'center',
					alignItems: 'center',
					flexDirection: 'column',
					padding: '24px 32px',
					marginTop:'24px'
				}}
			>
				<Box
					sx={{
						color: getColor('Gray 0'),
						...getStyle('Small_SemiBold'),
						mb: '16px',
					}}
				>
					Seamless Delivery Guaranteed!
				</Box>
				<Box
					sx={{
						borderLeft: `1px solid ${getColor('Success +1')}`,
						position: 'relative',
					}}
				>
					{lineOfProduct =='RENT' && <Box
						sx={{
							color: getColor('Gray 0'),
							...getStyle('Small_Regular'),
							ml: '12px',
							lineHeight: '16px',
							mb: '20px',
						}}
					>
						<Box sx={{ position: 'absolute', left: -8 }}>
							<TickMark />
						</Box>
						<Box sx={{ ml: '12px' }}>
							To ensure a seamless process, kindly complete your{' '}
							<span style={{ ...getStyle('Small_SemiBold') }}>
								KYC verification
							</span>{' '}
							after placing your order
							<Box
								sx={{
									color: getColor('Minty +1'),
									...getStyle('Tiny_SemiBold'),
									mt: '8px',
								}}
								onClick={openKycFaqDrawer}
							>
								Know More <KycArrow />
							</Box>
						</Box>
					</Box>}
					<Box
						sx={{
							color: getColor('Gray 0'),
							...getStyle('Small_Regular'),
							ml: '12px',
							lineHeight: '16px',
							mb: '20px',
						}}
					>
						<Box sx={{ position: 'absolute', left: -8 }}>
							<TickMark />
						</Box>
						 <Box sx={{ ml: '12px' }}>
							<span style={{ ...getStyle('Small_SemiBold') }}>
								Select your delivery date
							</span>{' '}
							at your convenience {lineOfProduct == 'RENT' ? 'after KYC completion':''} once the order is
							placed!
							<Box
								sx={{
									color: getColor('Minty +1'),
									...getStyle('Tiny_SemiBold'),
									mt: '8px',
								}}
								onClick={openDeliveryFaqDrawer}
							>
								Know More <KycArrow />
							</Box>
						</Box>
					</Box>
					<Box
						sx={{
							color: getColor('Gray 0'),
							...getStyle('Small_SemiBold'),
							ml: '12px',
							lineHeight: '16px',
							mb: '20px',
						}}
					>
						<Box sx={{ position: 'absolute', left: -10 }}>
							<DeliveryTruck />
						</Box>
						<Box sx={{ ml: '12px' }}>Delivery</Box>
						<Box
							sx={{
								position: 'absolute',
								bottom: 0,
								left: -1,
								height: '18px',
								width: '1px',
								background: 'white',
							}}
						>
							&nbsp;
						</Box>
					</Box>
				</Box>
			</FlexBox>
			
			<Drawer
				open={isDeliveryDrawerVisible}
				onClose={closeDeliveryFaqDrawer}
				anchor='right'
				PaperProps={{
					sx:{ width: '460px', p: '80px' }
				}}
			>
				<Box mt='40px'>
				<Box sx={{ borderRadius: '24px ' }}>
					<FlexBox
						sx={{
							justifyContent: 'space-between',
						}}
					>
						<Box width='100%'>
						<FlexBox justifyContent='space-between' alignItems='center'>
							<DrawerTruck/>
							<IconButton onClick={closeDeliveryFaqDrawer}>
								<CloseIcon />
							</IconButton>
						</FlexBox>
						<Typography mt='32px' variant='H9A_Regular'>
							Delivery Slots
						</Typography>
					</Box>
					</FlexBox>

					<Box sx={{ padding: '24px 0 48px 0' }}>
				
				
					<Box sx={{ padding: '0px', ...getStyle('Small_Regular') }}>
						Earliest delivery by{' '}
						<span
							style={{
								...getStyle('Small_SemiBold'),
								color: getColor('Gray 0'),
							}}
						>
							{promisedDate}
						</span>{' '}
						to{' '}
						<span
							style={{
								...getStyle('Small_SemiBold'),
								color: getColor('Gray 0'),
							}}
						>
							{selectedLocation?.pincode}
						</span>
					</Box>
					<Box sx={{ padding: '24px 0px 8px 0px', ...getStyle('P_Medium') }}>
						You set the delivery date!
					</Box>
					<Box
						sx={{
							...getStyle('Small_Regular'),
							color: getColor('Gray -2'),
							pb: '18px',
						}}
					>
						Choose your preferred delivery date after checkout by downloading the app for added convenience. 
					</Box>
					<Box
						sx={{
							...getStyle('Small_Regular'),
							color: getColor('Gray 0'),
							pb: '18px',
						}}
					>
						We&apos;ll take care of the rest and reach out to confirm your delivery two days in advance, ensuring everything goes smoothly.
					</Box>
					<Box
						sx={{
							...getStyle('Small_Regular'),
							color: getColor('Gray 0'),
							pb: '18px',
						}}
					>
						No confirmation needed for your 48-hour delivery. We&apos;ll arrive directly on the selected delivery date.
					</Box>
					<Box
						sx={{
							...getStyle('Small_SemiBold'),
							color: getColor('Gray 0'),
							pb: '18px',
						}}
					>
						{`Choose your delivery date after ordering!`}
					</Box>
				</Box>
				</Box>
			</Box>
			</Drawer>
		</>
	);
};

export default KycRequirementInfoCard;
